import Dexie, { Table } from 'dexie';
import type { PaletteAccessControl } from './permissions';

export interface ColorRecord {
  id?: string;
  format: string;
  values: number[];
  name?: string;
  metadata?: {
    deltaE?: number;
    gamut?: string;
    [key: string]: unknown;
  };
  history?: Array<{ timestamp: number; changes: string }>;
  created: number;
}

export interface PaletteAuditMetadata {
  timestamp: number;
  target: 'aa' | 'aaa' | 'wcag22';
  textSizePx: number;
  isBold: boolean;
  passRate: number;
  failCount: number;
  totalPairs: number;
  worstRatio: number | null;
}

export interface PaletteRecord {
  id?: string;
  name: string;
  colors: string[];
  lockedColors?: number[]; // Indices of locked colors that shouldn't be shuffled
  tags?: string[];
  created: number;
  modified: number;
  version: number;
  author?: string;
  lastAudit?: PaletteAuditMetadata;
}

export interface ProfileRecord {
  id?: string;
  type: string;
  iccData?: Blob;
  source: string;
  description?: string;
  created: number;
}

export interface PaletteHistoryRecord {
  id?: string;
  paletteId: string;
  version: number;
  colors: string[];
  name: string;
  timestamp: number;
  changeDescription?: string;
  author?: string;
}

export interface UserSettings {
  id?: string;
  roles: string[];
  settings: {
    theme?: 'light' | 'dark' | 'system';
    precision?: number;
    defaultColorSpace?: string;
    [key: string]: unknown;
  };
}

export interface ColorHistoryRecord {
  id?: string;
  hex: string;
  format: string;
  displayValue: string;
  timestamp: number;
  source?: 'manual' | 'eyedropper' | 'import' | 'palette';
}

export interface ColorFavoriteRecord {
  id?: string;
  hex: string;
  name?: string;
  tags?: string[];
  created: number;
  lastUsed?: number;
  notes?: string;
}

export interface ClipboardPresetRecord {
  id?: string;
  name: string;
  format: string;
  template: string;
  created: number;
  lastUsed?: number;
  isDefault?: boolean;
}

export type ActivityType = 
  | 'palette_created'
  | 'palette_updated'
  | 'palette_deleted'
  | 'palette_imported'
  | 'palette_exported'
  | 'palette_audited'
  | 'palette_compared'
  | 'colors_added'
  | 'colors_removed'
  | 'tags_modified';

export interface ActivityLogRecord {
  id?: string;
  type: ActivityType;
  paletteId?: string;
  paletteName?: string;
  author: string;
  timestamp: number;
  details?: {
    colorCount?: number;
    addedColors?: string[];
    removedColors?: string[];
    addedTags?: string[];
    removedTags?: string[];
    exportFormat?: string;
    importFormat?: string;
    auditPassRate?: number;
    comparedWith?: string;
    [key: string]: unknown;
  };
  description: string;
}

export interface SyncMetadata {
  id?: string;
  userId?: string; // Supabase user ID
  lastSyncTimestamp?: number;
  syncEnabled: boolean;
  autoSync: boolean;
  syncStatus: 'idle' | 'syncing' | 'error' | 'success';
  lastSyncError?: string;
  conflictResolution: 'local' | 'remote' | 'newest';
  created: number;
  modified: number;
}

export interface PaletteAccessRecord extends PaletteAccessControl {
  id?: string;
  created: number;
  modified: number;
}

export class ColorStudioDB extends Dexie {
  colors!: Table<ColorRecord, string>;
  palettes!: Table<PaletteRecord, string>;
  profiles!: Table<ProfileRecord, string>;
  users!: Table<UserSettings, string>;
  paletteHistory!: Table<PaletteHistoryRecord, string>;
  colorHistory!: Table<ColorHistoryRecord, string>;
  colorFavorites!: Table<ColorFavoriteRecord, string>;
  clipboardPresets!: Table<ClipboardPresetRecord, string>;
  activityLog!: Table<ActivityLogRecord, string>;
  syncMetadata!: Table<SyncMetadata, string>;
  paletteAccess!: Table<PaletteAccessRecord, string>;

  constructor() {
    super('ColorStudioDB');

    this.version(1).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
    });

    // Version 2: Add palette history tracking
    this.version(2).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
      paletteHistory: 'id, paletteId, version, timestamp',
    });

    // Version 3: Add color history, favorites, and clipboard presets
    this.version(3).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
      paletteHistory: 'id, paletteId, version, timestamp',
      colorHistory: 'id, hex, timestamp',
      colorFavorites: 'id, hex, *tags, created, lastUsed',
      clipboardPresets: 'id, name, created, lastUsed, isDefault',
    });

    // Version 4: Add activity logging
    this.version(4).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
      paletteHistory: 'id, paletteId, version, timestamp',
      colorHistory: 'id, hex, timestamp',
      colorFavorites: 'id, hex, *tags, created, lastUsed',
      clipboardPresets: 'id, name, created, lastUsed, isDefault',
      activityLog: 'id, type, paletteId, author, timestamp',
    });

    // Version 5: Add sync metadata
    this.version(5).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
      paletteHistory: 'id, paletteId, version, timestamp',
      colorHistory: 'id, hex, timestamp',
      colorFavorites: 'id, hex, *tags, created, lastUsed',
      clipboardPresets: 'id, name, created, lastUsed, isDefault',
      activityLog: 'id, type, paletteId, author, timestamp',
      syncMetadata: 'id, userId, syncEnabled, created, modified',
    });

    // Version 6: Add palette access control
    this.version(6).stores({
      colors: 'id, format, name, created',
      palettes: 'id, name, *tags, created, modified',
      profiles: 'id, type, source, created',
      users: 'id',
      paletteHistory: 'id, paletteId, version, timestamp',
      colorHistory: 'id, hex, timestamp',
      colorFavorites: 'id, hex, *tags, created, lastUsed',
      clipboardPresets: 'id, name, created, lastUsed, isDefault',
      activityLog: 'id, type, paletteId, author, timestamp',
      syncMetadata: 'id, userId, syncEnabled, created, modified',
      paletteAccess: 'id, paletteId, ownerId, isPublic, created, modified',
    });
  }

  async addColor(color: Omit<ColorRecord, 'id' | 'created'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.colors.add({
      ...color,
      id,
      created: Date.now(),
    });
    return id;
  }

  async addPalette(palette: Omit<PaletteRecord, 'id' | 'created' | 'modified' | 'version'>): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    const author = await this.getUsername();
    const userSettings = await this.getUserSettings();
    const userId = userSettings.id!;
    
    await this.palettes.add({
      ...palette,
      id,
      created: now,
      modified: now,
      version: 1,
      author,
    });
    
    // Create access control record with current user as owner
    await this.createPaletteAccess(id, userId);
    
    // Log activity
    await this.logActivity({
      type: 'palette_created',
      paletteId: id,
      paletteName: palette.name,
      description: `Created palette "${palette.name}" with ${palette.colors.length} colors`,
      details: {
        colorCount: palette.colors.length,
      },
    });
    
    return id;
  }

  async updatePalette(id: string, updates: Partial<PaletteRecord>): Promise<void> {
    const palette = await this.palettes.get(id);
    if (!palette) throw new Error('Palette not found');

    const newVersion = palette.version + 1;
    const now = Date.now();
    const author = await this.getUsername();

    // Save current state to history before updating
    await this.savePaletteHistory({
      paletteId: id,
      version: palette.version,
      colors: palette.colors,
      name: palette.name,
      timestamp: palette.modified,
      author: palette.author,
    });

    await this.palettes.update(id, {
      ...updates,
      modified: now,
      version: newVersion,
      author,
    });
    
    // Log activity based on what changed
    const changes: string[] = [];
    const details: Record<string, unknown> = {};
    
    if (updates.colors && JSON.stringify(updates.colors) !== JSON.stringify(palette.colors)) {
      const added = updates.colors.filter(c => !palette.colors.includes(c));
      const removed = palette.colors.filter(c => !updates.colors!.includes(c));
      
      if (added.length > 0) {
        changes.push(`added ${added.length} color(s)`);
        details.addedColors = added;
      }
      if (removed.length > 0) {
        changes.push(`removed ${removed.length} color(s)`);
        details.removedColors = removed;
      }
      details.colorCount = updates.colors.length;
    }
    
    if (updates.tags && JSON.stringify(updates.tags) !== JSON.stringify(palette.tags)) {
      const oldTags = palette.tags || [];
      const newTags = updates.tags || [];
      const added = newTags.filter(t => !oldTags.includes(t));
      const removed = oldTags.filter(t => !newTags.includes(t));
      
      if (added.length > 0) {
        changes.push(`added tag(s): ${added.join(', ')}`);
        details.addedTags = added;
      }
      if (removed.length > 0) {
        changes.push(`removed tag(s): ${removed.join(', ')}`);
        details.removedTags = removed;
      }
    }
    
    if (updates.name && updates.name !== palette.name) {
      changes.push(`renamed to "${updates.name}"`);
    }
    
    const description = changes.length > 0 
      ? `Updated palette "${updates.name || palette.name}": ${changes.join(', ')}`
      : `Updated palette "${updates.name || palette.name}"`;
    
    await this.logActivity({
      type: 'palette_updated',
      paletteId: id,
      paletteName: updates.name || palette.name,
      description,
      details,
    });
  }

  async deletePalette(id: string): Promise<void> {
    const palette = await this.palettes.get(id);
    if (palette) {
      await this.logActivity({
        type: 'palette_deleted',
        paletteId: id,
        paletteName: palette.name,
        description: `Deleted palette "${palette.name}"`,
        details: {
          colorCount: palette.colors.length,
        },
      });
    }
    
    await this.palettes.delete(id);
    await this.deletePaletteHistory(id);
    await this.deletePaletteAccess(id);
  }

  async getPalette(id: string): Promise<PaletteRecord | undefined> {
    return await this.palettes.get(id);
  }

  async getAllPalettes(): Promise<PaletteRecord[]> {
    return await this.palettes.orderBy('modified').reverse().toArray();
  }

  async searchPalettes(query: string): Promise<PaletteRecord[]> {
    const lowerQuery = query.toLowerCase();
    return await this.palettes
      .filter(
        (palette) =>
          palette.name.toLowerCase().includes(lowerQuery) ||
          (palette.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery)) ?? false)
      )
      .toArray();
  }

  async addProfile(profile: Omit<ProfileRecord, 'id' | 'created'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.profiles.add({
      ...profile,
      id,
      created: Date.now(),
    });
    return id;
  }

  async getUserSettings(): Promise<UserSettings> {
    const existing = await this.users.toArray();
    if (existing.length > 0) {
      return existing[0];
    }

    const defaultSettings: UserSettings = {
      id: crypto.randomUUID(),
      roles: ['editor'],
      settings: {
        theme: 'system',
        precision: 2,
        defaultColorSpace: 'hex',
        username: 'User',
      },
    };

    await this.users.add(defaultSettings);
    return defaultSettings;
  }

  async getUsername(): Promise<string> {
    const settings = await this.getUserSettings();
    return settings.settings.username as string || 'User';
  }

  async updateUserSettings(settings: Partial<UserSettings['settings']>): Promise<void> {
    const user = await this.getUserSettings();
    await this.users.update(user.id!, {
      settings: { ...user.settings, ...settings },
    });
  }

  async exportData(): Promise<string> {
    const data = {
      colors: await this.colors.toArray(),
      palettes: await this.palettes.toArray(),
      profiles: await this.profiles.toArray(),
      users: await this.users.toArray(),
      exportDate: new Date().toISOString(),
      version: '1.0',
    };

    return JSON.stringify(data, null, 2);
  }

  async importData(jsonData: string): Promise<void> {
    try {
      const data = JSON.parse(jsonData);

      if (data.colors) {
        await this.colors.bulkPut(data.colors);
      }
      if (data.palettes) {
        await this.palettes.bulkPut(data.palettes);
      }
      if (data.profiles) {
        await this.profiles.bulkPut(data.profiles);
      }
    } catch (error) {
      throw new Error(`Import failed: ${error}`);
    }
  }

  async clearAllData(): Promise<void> {
    await this.colors.clear();
    await this.palettes.clear();
    await this.profiles.clear();
    await this.paletteHistory.clear();
  }

  async savePaletteHistory(history: Omit<PaletteHistoryRecord, 'id'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.paletteHistory.add({
      ...history,
      id,
    });
    return id;
  }

  async getPaletteHistory(paletteId: string): Promise<PaletteHistoryRecord[]> {
    return await this.paletteHistory
      .where('paletteId')
      .equals(paletteId)
      .sortBy('version');
  }

  async getPaletteHistoryByVersion(paletteId: string, version: number): Promise<PaletteHistoryRecord | undefined> {
    return await this.paletteHistory
      .where(['paletteId', 'version'])
      .equals([paletteId, version])
      .first();
  }

  async deletePaletteHistory(paletteId: string): Promise<void> {
    await this.paletteHistory.where('paletteId').equals(paletteId).delete();
  }

  // Color History Methods
  async addToHistory(record: Omit<ColorHistoryRecord, 'id' | 'timestamp'>): Promise<string> {
    const id = crypto.randomUUID();
    const timestamp = Date.now();
    
    // Check if this color already exists in recent history
    const existing = await this.colorHistory
      .where('hex')
      .equals(record.hex)
      .and(item => timestamp - item.timestamp < 60000) // Within last minute
      .first();
    
    if (existing) {
      // Update timestamp instead of creating duplicate
      await this.colorHistory.update(existing.id!, { timestamp });
      return existing.id!;
    }
    
    await this.colorHistory.add({
      ...record,
      id,
      timestamp,
    });
    
    // Keep only last 100 history items
    const count = await this.colorHistory.count();
    if (count > 100) {
      const oldest = await this.colorHistory.orderBy('timestamp').limit(count - 100).toArray();
      await this.colorHistory.bulkDelete(oldest.map(item => item.id!));
    }
    
    return id;
  }

  async getHistory(limit: number = 50): Promise<ColorHistoryRecord[]> {
    return await this.colorHistory.orderBy('timestamp').reverse().limit(limit).toArray();
  }

  async clearHistory(): Promise<void> {
    await this.colorHistory.clear();
  }

  // Color Favorites Methods
  async addFavorite(favorite: Omit<ColorFavoriteRecord, 'id' | 'created'>): Promise<string> {
    // Check if already favorited
    const existing = await this.colorFavorites.where('hex').equals(favorite.hex).first();
    if (existing) {
      // Update lastUsed
      await this.colorFavorites.update(existing.id!, { lastUsed: Date.now() });
      return existing.id!;
    }
    
    const id = crypto.randomUUID();
    await this.colorFavorites.add({
      ...favorite,
      id,
      created: Date.now(),
    });
    return id;
  }

  async removeFavorite(id: string): Promise<void> {
    await this.colorFavorites.delete(id);
  }

  async getFavorites(): Promise<ColorFavoriteRecord[]> {
    return await this.colorFavorites.orderBy('created').reverse().toArray();
  }

  async isFavorite(hex: string): Promise<boolean> {
    const count = await this.colorFavorites.where('hex').equals(hex).count();
    return count > 0;
  }

  async updateFavorite(id: string, updates: Partial<ColorFavoriteRecord>): Promise<void> {
    await this.colorFavorites.update(id, updates);
  }

  // Clipboard Presets Methods
  async addClipboardPreset(preset: Omit<ClipboardPresetRecord, 'id' | 'created'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.clipboardPresets.add({
      ...preset,
      id,
      created: Date.now(),
    });
    return id;
  }

  async getClipboardPresets(): Promise<ClipboardPresetRecord[]> {
    return await this.clipboardPresets.orderBy('name').toArray();
  }

  async updateClipboardPreset(id: string, updates: Partial<ClipboardPresetRecord>): Promise<void> {
    await this.clipboardPresets.update(id, updates);
  }

  async deleteClipboardPreset(id: string): Promise<void> {
    await this.clipboardPresets.delete(id);
  }

  async getDefaultClipboardPreset(): Promise<ClipboardPresetRecord | undefined> {
    return await this.clipboardPresets.where('isDefault').equals(1).first();
  }

  // Activity Log Methods
  async logActivity(activity: Omit<ActivityLogRecord, 'id' | 'timestamp' | 'author'>): Promise<string> {
    const id = crypto.randomUUID();
    const timestamp = Date.now();
    const author = await this.getUsername();
    
    await this.activityLog.add({
      ...activity,
      id,
      timestamp,
      author,
    });
    
    // Keep only last 1000 activity items
    const count = await this.activityLog.count();
    if (count > 1000) {
      const oldest = await this.activityLog.orderBy('timestamp').limit(count - 1000).toArray();
      await this.activityLog.bulkDelete(oldest.map(item => item.id!));
    }
    
    return id;
  }

  async getActivityLog(limit: number = 100): Promise<ActivityLogRecord[]> {
    return await this.activityLog.orderBy('timestamp').reverse().limit(limit).toArray();
  }

  async getActivityLogByPalette(paletteId: string, limit: number = 50): Promise<ActivityLogRecord[]> {
    return await this.activityLog
      .where('paletteId')
      .equals(paletteId)
      .reverse()
      .limit(limit)
      .toArray();
  }

  async getActivityLogByType(type: ActivityType, limit: number = 50): Promise<ActivityLogRecord[]> {
    return await this.activityLog
      .where('type')
      .equals(type)
      .reverse()
      .limit(limit)
      .toArray();
  }

  async clearActivityLog(): Promise<void> {
    await this.activityLog.clear();
  }

  // Sync Metadata Methods
  async getSyncMetadata(): Promise<SyncMetadata> {
    const existing = await this.syncMetadata.toArray();
    if (existing.length > 0) {
      return existing[0];
    }

    const defaultMetadata: SyncMetadata = {
      id: crypto.randomUUID(),
      syncEnabled: false,
      autoSync: false,
      syncStatus: 'idle',
      conflictResolution: 'newest',
      created: Date.now(),
      modified: Date.now(),
    };

    await this.syncMetadata.add(defaultMetadata);
    return defaultMetadata;
  }

  async updateSyncMetadata(updates: Partial<SyncMetadata>): Promise<void> {
    const metadata = await this.getSyncMetadata();
    await this.syncMetadata.update(metadata.id!, {
      ...updates,
      modified: Date.now(),
    });
  }

  async setSyncStatus(status: SyncMetadata['syncStatus'], error?: string): Promise<void> {
    await this.updateSyncMetadata({
      syncStatus: status,
      lastSyncError: error,
    });
  }

  async recordSuccessfulSync(): Promise<void> {
    await this.updateSyncMetadata({
      lastSyncTimestamp: Date.now(),
      syncStatus: 'success',
      lastSyncError: undefined,
    });
  }

  // Enhanced export with all tables including history and activity
  async exportAllData(): Promise<string> {
    const data = {
      colors: await this.colors.toArray(),
      palettes: await this.palettes.toArray(),
      profiles: await this.profiles.toArray(),
      users: await this.users.toArray(),
      paletteHistory: await this.paletteHistory.toArray(),
      colorHistory: await this.colorHistory.toArray(),
      colorFavorites: await this.colorFavorites.toArray(),
      clipboardPresets: await this.clipboardPresets.toArray(),
      activityLog: await this.activityLog.toArray(),
      paletteAccess: await this.paletteAccess.toArray(),
      exportDate: new Date().toISOString(),
      version: '2.1',
    };

    return JSON.stringify(data, null, 2);
  }

  // Enhanced import with all tables
  async importAllData(jsonData: string): Promise<void> {
    try {
      const data = JSON.parse(jsonData);

      if (data.colors) await this.colors.bulkPut(data.colors);
      if (data.palettes) await this.palettes.bulkPut(data.palettes);
      if (data.profiles) await this.profiles.bulkPut(data.profiles);
      if (data.paletteHistory) await this.paletteHistory.bulkPut(data.paletteHistory);
      if (data.colorHistory) await this.colorHistory.bulkPut(data.colorHistory);
      if (data.colorFavorites) await this.colorFavorites.bulkPut(data.colorFavorites);
      if (data.clipboardPresets) await this.clipboardPresets.bulkPut(data.clipboardPresets);
      if (data.activityLog) await this.activityLog.bulkPut(data.activityLog);
      if (data.paletteAccess) await this.paletteAccess.bulkPut(data.paletteAccess);
    } catch (error) {
      throw new Error(`Import failed: ${error}`);
    }
  }

  // Palette Access Control Methods
  async createPaletteAccess(paletteId: string, ownerId: string): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    
    await this.paletteAccess.add({
      id,
      paletteId,
      ownerId,
      sharedWith: [],
      isPublic: false,
      created: now,
      modified: now,
    });
    
    return id;
  }

  async getPaletteAccess(paletteId: string): Promise<PaletteAccessRecord | undefined> {
    return await this.paletteAccess.where('paletteId').equals(paletteId).first();
  }

  async updatePaletteAccess(
    paletteId: string,
    updates: Partial<Omit<PaletteAccessRecord, 'id' | 'paletteId' | 'created' | 'modified'>>
  ): Promise<void> {
    const access = await this.getPaletteAccess(paletteId);
    if (!access) {
      throw new Error('Palette access record not found');
    }

    await this.paletteAccess.update(access.id!, {
      ...updates,
      modified: Date.now(),
    });
  }

  async deletePaletteAccess(paletteId: string): Promise<void> {
    const access = await this.getPaletteAccess(paletteId);
    if (access) {
      await this.paletteAccess.delete(access.id!);
    }
  }

  async getUserAccessiblePalettes(userId: string): Promise<PaletteRecord[]> {
    // Get all access records where user is owner or has shared access
    const accessRecords = await this.paletteAccess.toArray();
    
    const accessiblePaletteIds = accessRecords
      .filter(access => {
        // User is owner
        if (access.ownerId === userId) return true;
        
        // User has explicit shared access
        if (access.sharedWith?.some(share => share.userId === userId)) return true;
        
        // Palette is public
        if (access.isPublic) return true;
        
        return false;
      })
      .map(access => access.paletteId);

    // Get all palettes that match these IDs
    return await this.palettes
      .where('id')
      .anyOf(accessiblePaletteIds)
      .toArray();
  }

  async getPalettesOwnedBy(userId: string): Promise<PaletteRecord[]> {
    const accessRecords = await this.paletteAccess
      .where('ownerId')
      .equals(userId)
      .toArray();
    
    const paletteIds = accessRecords.map(access => access.paletteId);
    
    return await this.palettes
      .where('id')
      .anyOf(paletteIds)
      .toArray();
  }

  async getPalettesSharedWith(userId: string): Promise<PaletteRecord[]> {
    const accessRecords = await this.paletteAccess.toArray();
    
    const sharedPaletteIds = accessRecords
      .filter(access => 
        access.sharedWith?.some(share => share.userId === userId)
      )
      .map(access => access.paletteId);

    return await this.palettes
      .where('id')
      .anyOf(sharedPaletteIds)
      .toArray();
  }

  async getPublicPalettes(): Promise<PaletteRecord[]> {
    const accessRecords = await this.paletteAccess
      .where('isPublic')
      .equals(1)
      .toArray();
    
    const paletteIds = accessRecords.map(access => access.paletteId);
    
    return await this.palettes
      .where('id')
      .anyOf(paletteIds)
      .toArray();
  }
}

export const db = new ColorStudioDB();
